import {FocusEvent, MouseEvent, ReactNode} from "react";
import {css, cx} from "@panda/css";
import {visuallyHidden} from "@panda/patterns";

import {ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {priceM2Format} from "@pg-mono/string-utils";

import {PricesViewType} from "../../helpers/prices_view_type";
import {usePricesHubTableRowHover} from "../../hooks/use_prices_hub_table_row_hover";
import {trackPricesCityDistrictRowClick} from "../../tracking/track_prices_city_district_row_click";
import {DistrictRow} from "../../types/DistrictRow";
import {getDistrictListingLink} from "../../utils/get_district_listing_link";
import {PricesDistrictChangeBadge} from "./PricesDistrictChangeBadge";
import {PricesDistrictTableHoverRow} from "./PricesDistrictTableHoverRow";

interface Props {
    districts: DistrictRow[];
    citySlug: string;
    type: PricesViewType;
    dateLabel?: string;
    yearAgoDateLabel?: string;
    caption?: ReactNode;
}

export const PricesDistrictsTable = (props: Props) => {
    const {districts, citySlug, type, dateLabel, yearAgoDateLabel, caption} = props;
    const {hoveredRow: hoveredDistrict, isVisible, metrics, wrapperRef, showRow, hide} = usePricesHubTableRowHover(districts);

    const handleWrapperBlur = (event: FocusEvent<HTMLDivElement>) => {
        const nextTarget = event.relatedTarget;

        if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
            return;
        }

        hide();
    };

    const handleOverlayMouseMove = (event: MouseEvent<HTMLAnchorElement>) => {
        const tbody = wrapperRef.current?.querySelector("tbody");

        if (!tbody) {
            return;
        }

        const {clientY} = event;

        for (const rowEl of Array.from(tbody.rows)) {
            const rect = rowEl.getBoundingClientRect();

            if (clientY < rect.top || clientY >= rect.bottom) {
                continue;
            }

            const slug = rowEl.dataset.slug;

            if (slug) {
                showRow(slug, rowEl);
            }

            return;
        }
    };

    return (
        <div ref={wrapperRef} className={wrapperStyle} onMouseLeave={hide} onBlur={handleWrapperBlur}>
            <div className={tableFrameStyle}>
                <table className={tableStyle}>
                    {caption && (
                        <caption className={captionStyle}>
                            <Text variant="info_txt_1" as="span" color="var(--colors-gray-700)">
                                {caption}
                            </Text>
                        </caption>
                    )}

                    <thead>
                        <tr>
                            <th className={headerCellStyle}>
                                <Text variant="info_txt_1" as="p" color="var(--colors-gray-700)">
                                    Dzielnica
                                </Text>
                            </th>
                            <th className={headerCellStyle}>
                                <Text variant="info_txt_1" as="p" color="var(--colors-gray-700)" align="center">
                                    Aktualna Cena ofertowa{dateLabel ? ` (${dateLabel})` : ""}
                                </Text>
                            </th>
                            <th className={headerCellStyle}>
                                <Text variant="info_txt_1" as="p" color="var(--colors-gray-700)" align="center">
                                    Dane z zeszłego roku{yearAgoDateLabel ? ` (${yearAgoDateLabel})` : ""}
                                </Text>
                            </th>
                            <th className={headerCellStyle}>
                                <Text variant="info_txt_1" as="p" color="var(--colors-gray-700)" align="right">
                                    Trend cenowy (r/r)
                                </Text>
                            </th>
                            <th className={cx(headerCellStyle, chevronHeaderCellStyle)}>
                                <span className={visuallyHidden()}>Szczegóły</span>
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {districts.map((district) => {
                            const href = getDistrictListingLink(district, citySlug, type);

                            return (
                                <tr
                                    key={district.id}
                                    data-slug={district.slug}
                                    className={rowStyle}
                                    onMouseEnter={(event) => showRow(district.slug, event.currentTarget)}
                                    onFocus={(event) => showRow(district.slug, event.currentTarget)}
                                >
                                    <td className={cellStyle}>
                                        <a href={href} className={districtLinkStyle} onClick={() => trackPricesCityDistrictRowClick(district, href)}>
                                            <Text variant="body_copy_2" as="span">
                                                {district.name}
                                            </Text>
                                        </a>
                                    </td>

                                    <td className={cellStyle}>
                                        <Text variant="body_copy_2" align="center">
                                            {district.offerPriceM2 !== null ? priceM2Format(district.offerPriceM2) : "Brak danych"}
                                        </Text>
                                    </td>

                                    <td className={cellStyle}>
                                        <Text variant="body_copy_2" align="center">
                                            {district.yearAgoOfferPriceM2 !== null ? priceM2Format(district.yearAgoOfferPriceM2) : "Brak danych"}
                                        </Text>
                                    </td>

                                    <td className={cellStyle}>
                                        <div className={changeCellStyle}>
                                            <PricesDistrictChangeBadge yearOverYearPct={district.yearOverYearPct} />
                                        </div>
                                    </td>

                                    <td className={cx(cellStyle, chevronTdStyle)}>
                                        <div className={chevronCellStyle}>
                                            <a
                                                href={href}
                                                className={actionLinkStyle}
                                                aria-label={`Zobacz ceny mieszkań – ${district.name}`}
                                                onClick={() => trackPricesCityDistrictRowClick(district, href)}
                                            >
                                                <ChevronRightIcon size="1.4" fill="currentColor" />
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {hoveredDistrict && metrics ? (
                <PricesDistrictTableHoverRow
                    district={hoveredDistrict}
                    metrics={metrics}
                    isVisible={isVisible}
                    onMouseMove={handleOverlayMouseMove}
                    citySlug={citySlug}
                    type={type}
                />
            ) : null}
        </div>
    );
};

const wrapperStyle = css({
    display: "none",
    position: "relative",
    zIndex: 1,
    width: "calc(100% + 5.2rem)",
    maxWidth: "calc(100% + 5.2rem)",
    mx: "-2.6rem",
    px: "2.6rem",
    overflow: "visible",

    md: {
        display: "block"
    }
});

const tableFrameStyle = css({
    width: "100%",
    overflow: "visible",
    border: "1px solid var(--colors-gray-300)",
    borderRadius: 2
});

const tableStyle = css({
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,

    "& thead th": {
        borderBottom: "1px solid var(--colors-gray-300)"
    },

    "& tbody tr:not(:last-child) td": {
        borderBottom: "1px solid var(--colors-gray-300)"
    },

    "& thead th:first-child": {
        borderTopLeftRadius: 2
    },

    "& thead th:last-child": {
        borderTopRightRadius: 2
    },

    "& tbody tr:last-child td:first-child": {
        borderBottomLeftRadius: 2
    },

    "& tbody tr:last-child td:last-child": {
        borderBottomRightRadius: 2
    }
});

const captionStyle = css({
    captionSide: "top",
    textAlign: "center",
    px: 2,
    py: 1.5
});

const headerCellStyle = css({
    px: 2,
    py: 1.5
});

const chevronHeaderCellStyle = css({
    width: "6rem"
});

const rowStyle = css({
    color: "var(--colors-secondary)",
    cursor: "pointer",

    "&:nth-child(even)": {
        backgroundColor: "var(--colors-gray-200)"
    }
});

// Matches PricesHubTransactionPricesTable's cityLinkStyle - plain in-flow link around the district name,
// not stretched over the row (that's what the actionLinkStyle chevron link is for).
const districtLinkStyle = css({
    color: "inherit",
    textDecoration: "none",

    _hover: {
        color: "inherit",
        textDecoration: "underline"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    }
});

const cellStyle = css({
    px: 2,
    py: 2
});

const changeCellStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end"
});

const chevronTdStyle = css({
    width: "6rem"
});

const chevronCellStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

// Matches PricesHubTransactionPricesTable's actionLinkStyle - circular hit target around the chevron.
const actionLinkStyle = css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "3.2rem",
    height: "3.2rem",
    color: "var(--colors-gray-600)",
    borderRadius: "50%",

    _hover: {
        color: "var(--colors-gray-600)"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    }
});
import {FocusEvent, MouseEvent, ReactNode} from "react";
import {css, cx} from "@panda/css";
import {visuallyHidden} from "@panda/patterns";

import {ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {priceM2Format} from "@pg-mono/string-utils";

import {PricesViewType} from "../../helpers/prices_view_type";
import {usePricesHubTableRowHover} from "../../hooks/use_prices_hub_table_row_hover";
import {trackPricesCityDistrictRowClick} from "../../tracking/track_prices_city_district_row_click";
import {DistrictRow} from "../../types/DistrictRow";
import {getDistrictListingLink} from "../../utils/get_district_listing_link";
import {PricesDistrictChangeBadge} from "./PricesDistrictChangeBadge";
import {PricesDistrictTableHoverRow} from "./PricesDistrictTableHoverRow";

interface Props {
    districts: DistrictRow[];
    citySlug: string;
    type: PricesViewType;
    dateLabel?: string;
    yearAgoDateLabel?: string;
    caption?: ReactNode;
}

export const PricesDistrictsTable = (props: Props) => {
    const {districts, citySlug, type, dateLabel, yearAgoDateLabel, caption} = props;
    const {hoveredRow: hoveredDistrict, isVisible, metrics, wrapperRef, showRow, hide} = usePricesHubTableRowHover(districts);

    const handleWrapperBlur = (event: FocusEvent<HTMLDivElement>) => {
        const nextTarget = event.relatedTarget;

        if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
            return;
        }

        hide();
    };

    const handleOverlayMouseMove = (event: MouseEvent<HTMLAnchorElement>) => {
        const tbody = wrapperRef.current?.querySelector("tbody");

        if (!tbody) {
            return;
        }

        const {clientY} = event;

        for (const rowEl of Array.from(tbody.rows)) {
            const rect = rowEl.getBoundingClientRect();

            if (clientY < rect.top || clientY >= rect.bottom) {
                continue;
            }

            const slug = rowEl.dataset.slug;

            if (slug) {
                showRow(slug, rowEl);
            }

            return;
        }
    };

    return (
        <div ref={wrapperRef} className={wrapperStyle} onMouseLeave={hide} onBlur={handleWrapperBlur}>
            <div className={tableFrameStyle}>
                <table className={tableStyle}>
                    {caption && (
                        <caption className={captionStyle}>
                            <Text variant="info_txt_1" as="span" color="var(--colors-gray-700)">
                                {caption}
                            </Text>
                        </caption>
                    )}

                    <thead>
                        <tr>
                            <th className={headerCellStyle}>
                                <Text variant="info_txt_1" as="p" color="var(--colors-gray-700)">
                                    Dzielnica
                                </Text>
                            </th>
                            <th className={headerCellStyle}>
                                <Text variant="info_txt_1" as="p" color="var(--colors-gray-700)" align="center">
                                    Aktualna Cena ofertowa{dateLabel ? ` (${dateLabel})` : ""}
                                </Text>
                            </th>
                            <th className={headerCellStyle}>
                                <Text variant="info_txt_1" as="p" color="var(--colors-gray-700)" align="center">
                                    Dane z zeszłego roku{yearAgoDateLabel ? ` (${yearAgoDateLabel})` : ""}
                                </Text>
                            </th>
                            <th className={headerCellStyle}>
                                <Text variant="info_txt_1" as="p" color="var(--colors-gray-700)" align="right">
                                    Trend cenowy (r/r)
                                </Text>
                            </th>
                            <th className={cx(headerCellStyle, chevronHeaderCellStyle)}>
                                <span className={visuallyHidden()}>Szczegóły</span>
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {districts.map((district) => {
                            const href = getDistrictListingLink(district, citySlug, type);

                            return (
                                <tr
                                    key={district.id}
                                    data-slug={district.slug}
                                    className={rowStyle}
                                    onMouseEnter={(event) => showRow(district.slug, event.currentTarget)}
                                    onFocus={(event) => showRow(district.slug, event.currentTarget)}
                                >
                                    <td className={cellStyle}>
                                        <a href={href} className={districtLinkStyle} onClick={() => trackPricesCityDistrictRowClick(district, href)}>
                                            <Text variant="body_copy_2" as="span">
                                                {district.name}
                                            </Text>
                                        </a>
                                    </td>

                                    <td className={cellStyle}>
                                        <Text variant="body_copy_2" align="center">
                                            {district.offerPriceM2 !== null ? priceM2Format(district.offerPriceM2) : "Brak danych"}
                                        </Text>
                                    </td>

                                    <td className={cellStyle}>
                                        <Text variant="body_copy_2" align="center">
                                            {district.yearAgoOfferPriceM2 !== null ? priceM2Format(district.yearAgoOfferPriceM2) : "Brak danych"}
                                        </Text>
                                    </td>

                                    <td className={cellStyle}>
                                        <div className={changeCellStyle}>
                                            <PricesDistrictChangeBadge yearOverYearPct={district.yearOverYearPct} />
                                        </div>
                                    </td>

                                    <td className={cx(cellStyle, chevronTdStyle)}>
                                        <div className={chevronCellStyle}>
                                            <a
                                                href={href}
                                                className={actionLinkStyle}
                                                aria-label={`Zobacz ceny mieszkań – ${district.name}`}
                                                onClick={() => trackPricesCityDistrictRowClick(district, href)}
                                            >
                                                <ChevronRightIcon size="1.4" fill="currentColor" />
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {hoveredDistrict && metrics ? (
                <PricesDistrictTableHoverRow
                    district={hoveredDistrict}
                    metrics={metrics}
                    isVisible={isVisible}
                    onMouseMove={handleOverlayMouseMove}
                    citySlug={citySlug}
                    type={type}
                />
            ) : null}
        </div>
    );
};

const wrapperStyle = css({
    display: "none",
    position: "relative",
    zIndex: 1,
    width: "calc(100% + 5.2rem)",
    maxWidth: "calc(100% + 5.2rem)",
    mx: "-2.6rem",
    px: "2.6rem",
    overflow: "visible",

    md: {
        display: "block"
    }
});

const tableFrameStyle = css({
    width: "100%",
    overflow: "visible",
    border: "1px solid var(--colors-gray-300)",
    borderRadius: 2
});

const tableStyle = css({
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,

    "& thead th": {
        borderBottom: "1px solid var(--colors-gray-300)"
    },

    "& tbody tr:not(:last-child) td": {
        borderBottom: "1px solid var(--colors-gray-300)"
    },

    "& thead th:first-child": {
        borderTopLeftRadius: 2
    },

    "& thead th:last-child": {
        borderTopRightRadius: 2
    },

    "& tbody tr:last-child td:first-child": {
        borderBottomLeftRadius: 2
    },

    "& tbody tr:last-child td:last-child": {
        borderBottomRightRadius: 2
    }
});

const captionStyle = css({
    captionSide: "top",
    textAlign: "center",
    px: 2,
    py: 1.5
});

const headerCellStyle = css({
    px: 2,
    py: 1.5
});

const chevronHeaderCellStyle = css({
    width: "6rem"
});

const rowStyle = css({
    color: "var(--colors-secondary)",
    cursor: "pointer",

    "&:nth-child(even)": {
        backgroundColor: "var(--colors-gray-200)"
    }
});

// Matches PricesHubTransactionPricesTable's cityLinkStyle - plain in-flow link around the district name,
// not stretched over the row (that's what the actionLinkStyle chevron link is for).
const districtLinkStyle = css({
    color: "inherit",
    textDecoration: "none",

    _hover: {
        color: "inherit",
        textDecoration: "underline"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    }
});

const cellStyle = css({
    px: 2,
    py: 2
});

const changeCellStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end"
});

const chevronTdStyle = css({
    width: "6rem"
});

const chevronCellStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

// Matches PricesHubTransactionPricesTable's actionLinkStyle - circular hit target around the chevron.
const actionLinkStyle = css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "3.2rem",
    height: "3.2rem",
    color: "var(--colors-gray-600)",
    borderRadius: "50%",

    _hover: {
        color: "var(--colors-gray-600)"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    }
});
